import { ref, computed, reactive } from 'vue';
import { cloneDeep } from 'lodash';
import { graphql } from '@/gql';

import util from '@/Utils/util';
import { queryExecute, mutationExecute } from '@/Utils/graphqlAPI';
import { FlaggedUserType } from '@/models/caller';
import type { CurrentUser } from '@/models/user';
import { viewVirtualDashboardKey } from '@/constants/app';

import '@/services/partners/fragments/partnerFieldConfigFragment';

const currentUser = ref({} as CurrentUser);
const favoritePages = ref([] as string[]);
const favoriteForms = ref([] as string[]);

export const useProfile = () => {
  const userFullName = computed(() => util.getFullName(currentUser.value?.firstName, currentUser.value?.lastName));

  const isFraudFamilyCustomer = computed(() => {
    if (!currentUser.value?.id) {
      return false;
    }

    if (!userRoles.isFamilyUser && !userRoles.isCustomerUser) {
      return false;
    }

    return currentUser.value.flagged === FlaggedUserType.FraudUser;
  });

  const isDeactivatedFamilyCustomer = computed(() => {
    if (!currentUser.value?.id) {
      return false;
    }

    if (!userRoles.isFamilyUser && !userRoles.isCustomerUser) {
      return false;
    }

    return !currentUser.value.isActive;
  });

  const userRoles: {
    roles: string[];
    isAdmin: boolean;
    isSupervisor: boolean;
    isRideOperator: boolean;
    isGourmetOperator: boolean;
    isGourmetSupervisor: boolean;
    isGourmetTeamLead: boolean;
    isDirector: boolean;
    isGeneralTeamLead: boolean;
    isTeamLead: boolean;
    isOnboardingTeamLead: boolean;
    isRideTeamLead: boolean;
    isGroceryOperator: boolean;
    isGroceryManager: boolean;
    isGroceryTeamLead: boolean;
    isCustomerUser: boolean;
    isFamilyUser: boolean;
    isLossPrevention: boolean;
    isLossPreventionGourmet: boolean;
    isQualityAssurance: boolean;
    isPartnershipOperator: boolean;
    isPartnerAdmin: boolean;
    isD2DOperator: boolean;
    isBookingsOperator: boolean;
    isLossPreventionGrocery: boolean;
    isVirtualAssistanceGrocery: boolean;
    isPremiumPartner: boolean;
    isGoGoEmployee: boolean;
    isAffiliateUser: boolean;
    isAffiliateManager: boolean;
    isHigherThanOperator: boolean;
    isManagementRole: boolean;
    isTrainingOperator: boolean;
    isGoGoCareOperator: boolean;
    isHomeServicesOperator: boolean;
    isHomeServicesRegistrationsOperator: boolean;
    isFacebookLeadsRegistrationsOperator: boolean;
    isResearchOperator: boolean;
    isPartnershipCallerOperator: boolean;
    isCozyOperator: boolean;
    isGoGoWelcomeOperator: boolean;
    isHigherThanTeamLead: boolean;
    isSupervisorTeamLead: boolean;
  } = reactive({
    roles: computed(() => currentUser.value?.roles || []),
    isAdmin: computed(() => userRoles.roles.includes('admin')),
    isSupervisor: computed(() => userRoles.roles.includes('supervisor')),
    isRideOperator: computed(() => userRoles.roles.includes('operator')),
    isGourmetOperator: computed(() => userRoles.roles.includes('gourmet-operator')),
    isGourmetSupervisor: computed(() => userRoles.roles.includes('gourmet-supervisor')),
    isGourmetTeamLead: computed(() => userRoles.roles.includes('gourmet-supervisor')),
    isDirector: computed(() => userRoles.roles.includes('director')),
    isGeneralTeamLead: computed(() => userRoles.roles.includes('teamLead')),
    isOnboardingTeamLead: computed(() => userRoles.roles.includes('onboarding-team-lead')),
    isRideTeamLead: computed(() => userRoles.roles.includes('rideTeamLead')),
    isGroceryOperator: computed(() => userRoles.roles.includes('grocery-operator')),
    isGroceryManager: computed(() => userRoles.roles.includes('grocery-manager')),
    isGroceryTeamLead: computed(() => userRoles.roles.includes('grocery-manager')),
    isCustomerUser: computed(() => userRoles.roles.includes('customer')),
    isFamilyUser: computed(() => userRoles.roles.includes('family')),
    isLossPrevention: computed(() => userRoles.roles.includes('lossPrevention')),
    isLossPreventionGourmet: computed(() => userRoles.roles.includes('lossPreventionGourmet')),
    isQualityAssurance: computed(() => userRoles.roles.includes('qualityAssurance')),
    isPartnershipOperator: computed(() => userRoles.roles.includes('partnership-operator')),
    isPartnerAdmin: computed(() => userRoles.roles.includes('partner')),
    isD2DOperator: computed(() => userRoles.roles.includes('doortodoor-operator')),
    isBookingsOperator: computed(() => userRoles.roles.includes('bookings-operator')),
    isHomeServicesOperator: computed(() => userRoles.roles.includes('homeServices-operator')),
    isHomeServicesRegistrationsOperator: computed(() => userRoles.roles.includes('homeServices-registrations-operator')),
    isFacebookLeadsRegistrationsOperator: computed(() => userRoles.roles.includes('facebookLeads-registrations-operator')),
    isLossPreventionGrocery: computed(() => userRoles.roles.includes('lossPreventionGrocery')),
    isVirtualAssistanceGrocery: computed(() => userRoles.roles.includes('virtualAssistanceGrocery')),
    isPremiumPartner: computed(() => userRoles.roles.includes('premium')),
    isGoGoEmployee: computed(() => !userRoles.isCustomerUser && !userRoles.isFamilyUser),
    isAffiliateUser: computed(() => userRoles.roles.includes('affiliate')),
    isAffiliateManager: computed(() => userRoles.roles.includes('affiliate-manager')),
    isGoGoCareOperator: computed(() => userRoles.roles.includes('gogocare-operator')),
    isResearchOperator: computed(() => userRoles.roles.includes('research-operator')),
    isPartnershipCallerOperator: computed(() => userRoles.roles.includes('partnership-caller-operator')),
    isCozyOperator: computed(() => userRoles.roles.includes('cozy-operator')),
    isGoGoWelcomeOperator: computed(() => userRoles.roles.includes('gogo-welcome-operator')),
    isSupervisorTeamLead: computed(() => userRoles.roles.includes('supervisorTeamLead')),
    isTeamLead: computed(
      () => userRoles.isGeneralTeamLead || userRoles.isRideTeamLead || userRoles.isGourmetTeamLead || userRoles.isGroceryTeamLead,
    ),
    isHigherThanOperator: computed(
      () =>
        userRoles.isAdmin ||
        userRoles.isDirector ||
        userRoles.isSupervisor ||
        userRoles.isTeamLead ||
        userRoles.isLossPrevention ||
        userRoles.isLossPreventionGourmet ||
        userRoles.isQualityAssurance ||
        userRoles.isLossPreventionGrocery,
    ),
    isManagementRole: computed(
      () => userRoles.isAdmin || userRoles.isDirector || userRoles.isSupervisor || userRoles.isTeamLead || userRoles.isOnboardingTeamLead,
    ),
    isTrainingOperator: computed(() => currentUser.value?.isTrainingOperator ?? false),
    isHigherThanTeamLead: computed(
      () =>
        userRoles.isAdmin ||
        userRoles.isDirector ||
        userRoles.isSupervisor ||
        userRoles.isGourmetSupervisor ||
        userRoles.isAffiliateManager,
    ),
  });

  const fetchCurrentUser = async () => {
    const response = await queryExecute({
      query: graphql(`
        query Me {
          me {
            id
            firstName
            lastName
            userName
            role
            about
            email
            phoneNumber
            lastLogin
            isActive
            logins
            profile
            roles
            languages
            flagged
            teamLeadId
            favoritePages
            partners {
              id
              codeName
              name
              isDisabled
              plan
              maxRides
              timeZone
              serviceCode
              isAutoConfirmSurge
              subsidyCondition
              isApprovedAddressPartner
              isSelfServeDashboard
              rideAllowance {
                tiers {
                  name
                  allowance
                }
              }
              fieldConfig {
                ...PartnerFieldConfigFragment
              }
            }
            affiliate {
              id
              name
              codeName
              extension
            }
            operatorTextNumber
            isNeedToUpdatePassword
            isTrainingOperator
            favoriteForms
          }
        }
      `),
      fetchPolicy: 'no-cache',
    });

    const isViewVirtualOperatorDashboard = !!(localStorage.getItem(viewVirtualDashboardKey) as string);
    const data: CurrentUser = response.data.me!; // TODO(cem): ENG-7105
    data.originalRoles = data.roles || [];

    // If the user is on the virtual operator dashboard, we need to manually set their role as an operator.
    data.roles = isViewVirtualOperatorDashboard ? ['operator'] : data.roles || [];

    localStorage.setItem('roles', JSON.stringify(data.roles));

    setCurrentUser(data);
    setFavoritePages(data.favoritePages || []);
    setFavoriteForms(data.favoriteForms || []);

    return data;
  };

  const setCurrentUser = (value: any) => {
    currentUser.value = cloneDeep(value);
  };

  const setFavoritePages = (value: string[]) => {
    favoritePages.value = cloneDeep(value);
  };

  const setFavoriteForms = (value: string[]) => {
    favoriteForms.value = cloneDeep(value);
  };

  const isOperatorOnlineForInboundCalls = async () => {
    const response = await queryExecute({
      query: graphql(`
        query IsOperatorOnlineForInboundCalls($id: String!) {
          isOperatorOnlineForInboundCalls(id: $id)
        }
      `),
      variables: {
        id: currentUser.value.id,
      },
      fetchPolicy: 'no-cache',
    });

    return response.data.isOperatorOnlineForInboundCalls;
  };

  const addFavoritePage = async (path: string) => {
    const response = await mutationExecute({
      mutation: graphql(`
        mutation AddFavoritePage($path: String!) {
          addFavoritePage(path: $path)
        }
      `),
      variables: {
        path,
      },
    });

    if (!response.data?.addFavoritePage) {
      return;
    }

    favoritePages.value.push(path);
  };

  const removeFavoritePage = async (path: string) => {
    const response = await mutationExecute({
      mutation: graphql(`
        mutation RemoveFavoritePage($path: String!) {
          removeFavoritePage(path: $path)
        }
      `),
      variables: {
        path,
      },
    });

    if (!response.data?.removeFavoritePage) {
      return;
    }

    const index = favoritePages.value.findIndex((item: string) => item === path);

    if (index === -1) {
      return;
    }

    favoritePages.value.splice(index, 1);
  };

  return {
    currentUser,
    userRoles,
    userFullName,
    isFraudFamilyCustomer,
    isDeactivatedFamilyCustomer,
    favoritePages,
    favoriteForms,

    setCurrentUser,
    fetchCurrentUser,
    isOperatorOnlineForInboundCalls,
    addFavoritePage,
    removeFavoritePage,
    setFavoriteForms,
  };
};
